/*
 * pdpf-runtime.js — the data layer for a wrapped generated PDP.
 *
 * The generated page ships its own markup, CSS and UI behaviour. What it CANNOT
 * ship is truthful commerce data: its prices, variant ids and availability are
 * literals baked in at generation time. This runtime replaces that layer.
 *
 * Everything here is driven off two things:
 *   1. <script type="application/json" data-pdpf-variants> — rendered by Liquid
 *      from the real product object, so prices and ids can never drift.
 *   2. The data-* hooks the generator already emits in the markup.
 *
 * Every hook is optional. A page without a gallery, or without a sticky bar,
 * simply does not get that behaviour — it never throws. This matters because
 * the runtime is shared across generated pages that differ in layout.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pdpf-root]');
  if (!root) return;

  // Looked up document-wide on purpose: the island is emitted as a sibling of the
  // lifted page wrapper, not inside it, and scoping this to root silently disables
  // the whole runtime.
  var dataEl = document.querySelector('script[type="application/json"][data-pdpf-variants]');
  if (!dataEl) {
    console.error('[pdpf] no [data-pdpf-variants] island found — commerce data unavailable');
    return;
  }

  var DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('[pdpf] variant data is not valid JSON', e);
    return;
  }

  var optionNames = DATA.optionNames || [];
  var variants = DATA.variants || [];
  var media = DATA.media || [];
  var cartAddUrl = DATA.cartAddUrl || '/cart/add.js';
  var cartUrl = DATA.cartUrl || '/cart';

  // Same element the theme's own product-form.js looks for, so this page opens
  // whichever cart UI the merchant has configured (Settings > Cart > Cart type)
  // instead of always redirecting to /cart. Null when cart_type is "page" — the
  // redirect fallback below is what that mode is supposed to do anyway.
  var cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  if (!variants.length) {
    console.warn('[pdpf] product has no variants — add to cart disabled');
  }

  /* ---------- selection state ---------- */
  // Seeded from whichever button the generator marked active, so the page's own
  // idea of a default (usually the best seller) is preserved rather than reset.
  var selection = {};
  var qty = 1;

  // Landing on the page is not the same as choosing a pack. Both the gallery's
  // opening slide and the ?variant= rewrite below key off these rather than off
  // the merely-defaulted selection, so an untouched page keeps the product's own
  // featured image and a clean URL.
  var arrivedWithVariant = /[?&]variant=/.test(window.location.search);
  var visitorPickedVariant = false;

  optionNames.forEach(function (name) {
    var group = root.querySelectorAll('[data-option-name="' + cssEscape(name) + '"]');
    if (!group.length) return;
    var active = null;
    group.forEach(function (b) {
      if (b.classList.contains('active') && !active) active = b;
    });
    var chosen = active || group[0];
    selection[name] = chosen.getAttribute('data-option-value');
  });

  // Any option the markup does not expose still needs a value or nothing will
  // ever resolve — fall back to the first variant's value for that option.
  if (variants.length) {
    optionNames.forEach(function (name, i) {
      if (selection[name] === undefined) selection[name] = variants[0].options[i];
    });
  }

  function cssEscape(s) {
    return String(s).replace(/["\\]/g, '\\$&');
  }

  /* ---------- variant resolution ---------- */
  function resolveVariant() {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var j = 0; j < optionNames.length; j++) {
        if (v.options[j] !== selection[optionNames[j]]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  // A variant's featured media, expressed as an index into the gallery. Matched
  // on media id rather than URL because image_url renders a different width for
  // the gallery than for the swatches, so the strings never compare equal.
  function mediaIndexForVariant(v) {
    if (!v || v.mediaId === undefined || v.mediaId === null) return -1;
    for (var i = 0; i < media.length; i++) {
      if (media[i].id === v.mediaId) return i;
    }
    return -1;
  }

  /* ---------- rendering ---------- */
  var priceEls = root.querySelectorAll('[data-pdpf-price], #bb-price, #sticky-price');
  var wasEls = root.querySelectorAll('[data-pdpf-compare-at], #bb-was');
  var saveEls = root.querySelectorAll('[data-pdpf-saved], #bb-save');
  var unitEls = root.querySelectorAll('[data-pdpf-unit], #bb-unit');
  var atcEls = root.querySelectorAll('[data-atc]');

  function render() {
    var v = resolveVariant();

    priceEls.forEach(function (el) {
      el.textContent = v ? v.priceFormatted : '—';
    });

    // Compare-at is per variant, so these have to be cleared as well as filled —
    // leaving a previous variant's was-price standing would misprice the page.
    wasEls.forEach(function (el) {
      el.textContent = v && v.compareAtFormatted ? v.compareAtFormatted : '';
    });
    saveEls.forEach(function (el) {
      el.textContent = v && v.savedPercent ? 'Save ' + v.savedPercent + '%' : '';
    });

    // Trailing separator lives with the unit price so the rest of the note reads
    // correctly on a single-pack variant, where there is no unit price to show.
    unitEls.forEach(function (el) {
      el.textContent = v && v.unitFormatted ? v.unitFormatted + ' per pack · ' : '';
    });

    // Per-option label readouts: the generator emits #sel-<option name>.
    optionNames.forEach(function (name) {
      var el = root.querySelector('#sel-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
      if (el) el.textContent = selection[name];
    });

    var summary = root.querySelector('#sticky-sel, [data-pdpf-selection]');
    if (summary) {
      summary.textContent = optionNames.map(function (n) { return selection[n]; }).join(' · ');
    }

    // Reflect active state on every button group, including duplicates such as
    // the bundle cards, so the two stay visually in sync.
    root.querySelectorAll('[data-option-name][data-option-value]').forEach(function (b) {
      var n = b.getAttribute('data-option-name');
      var val = b.getAttribute('data-option-value');
      if (b.hasAttribute('data-bundle')) return; // bundle cards are CTAs, not state
      var on = selection[n] === val;
      b.classList.toggle('active', on);
      if (b.hasAttribute('aria-pressed')) b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    atcEls.forEach(function (btn) {
      var buyable = !!(v && v.available);
      btn.disabled = !buyable;
      btn.setAttribute('aria-disabled', buyable ? 'false' : 'true');
      if (!buyable) {
        if (!btn.hasAttribute('data-pdpf-label')) {
          btn.setAttribute('data-pdpf-label', btn.textContent);
        }
        btn.textContent = v ? 'Sold out' : 'Unavailable';
      } else if (btn.hasAttribute('data-pdpf-cta') && v.ctaLabel) {
        // The offer differs per pack (free shipping above the threshold, savings
        // below it), so this label is variant data rather than a fixed string —
        // and it also restores the button after a sold-out variant was selected.
        btn.textContent = v.ctaLabel;
        btn.removeAttribute('data-pdpf-label');
      } else if (btn.hasAttribute('data-pdpf-label')) {
        btn.textContent = btn.getAttribute('data-pdpf-label');
        btn.removeAttribute('data-pdpf-label');
      }
    });

    // Keep the URL shareable and let a refresh land on the same variant — but only
    // after a real choice. Stamping it on load rewrote every clean product link
    // into a variant-specific one before the visitor had touched anything.
    if ((visitorPickedVariant || arrivedWithVariant) && v && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    }
  }

  /* ---------- option buttons ---------- */
  root.querySelectorAll('[data-option-name][data-option-value]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      selection[btn.getAttribute('data-option-name')] = btn.getAttribute('data-option-value');
      visitorPickedVariant = true;
      if (btn.hasAttribute('data-bundle')) {
        var box = root.querySelector('[data-pdpf-buybox], #akina-buybox');
        if (box && box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth' });
      }
      // Only on an explicit pick — doing this inside render() would yank the
      // gallery back every time a thumbnail was clicked.
      var picked = mediaIndexForVariant(resolveVariant());
      if (picked >= 0) setSlide(picked);
      render();
    });
  });

  /* ---------- quantity stepper ---------- */
  var qtyVal = root.querySelector('#qty-val, [data-pdpf-qty-value]');
  var qtyMinus = root.querySelector('#qty-minus, [data-pdpf-qty-minus]');
  var qtyPlus = root.querySelector('#qty-plus, [data-pdpf-qty-plus]');
  if (qtyMinus) qtyMinus.addEventListener('click', function () {
    if (qty > 1) { qty--; if (qtyVal) qtyVal.textContent = qty; }
  });
  if (qtyPlus) qtyPlus.addEventListener('click', function () {
    qty++; if (qtyVal) qtyVal.textContent = qty;
  });

  /* ---------- gallery ---------- */
  // Sourced from product.media so the gallery shows what the merchant actually
  // uploaded, not the generator's literal image list.
  var mainImg = root.querySelector('#gal-main-img, [data-pdpf-gallery-main]');
  var thumbs = root.querySelector('#gal-thumbs, [data-pdpf-gallery-thumbs]');
  var galIdx = 0;

  function setSlide(i) {
    if (!media.length || !mainImg) return;
    if (i < 0) i = media.length - 1;
    if (i >= media.length) i = 0;
    galIdx = i;
    // A srcset outranks src, so a responsive main image would keep showing slide 0
    // no matter what we assign here. Cleared rather than assumed absent.
    if (mainImg.hasAttribute('srcset')) mainImg.removeAttribute('srcset');
    mainImg.src = media[i].src;
    mainImg.alt = media[i].alt || '';
    if (thumbs) {
      thumbs.querySelectorAll('button').forEach(function (b, j) {
        b.classList.toggle('active', j === i);
      });
    }
  }

  if (thumbs && media.length) {
    thumbs.innerHTML = '';
    media.forEach(function (m, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'View image ' + (i + 1));
      if (i === 0) b.className = 'active';
      var im = document.createElement('img');
      // Thumbnails are ~68px on screen. Fall back to src so any other page
      // sharing this runtime with an older media payload keeps working.
      im.src = m.thumb || m.src;
      im.alt = m.alt || '';
      im.loading = 'lazy';
      b.appendChild(im);
      b.addEventListener('click', function () { setSlide(i); });
      thumbs.appendChild(b);
    });
  }

  var prev = root.querySelector('#gal-prev, [data-pdpf-gallery-prev]');
  var next = root.querySelector('#gal-next, [data-pdpf-gallery-next]');
  if (prev) prev.addEventListener('click', function () { setSlide(galIdx - 1); });
  if (next) next.addEventListener('click', function () { setSlide(galIdx + 1); });

  /* ---------- gallery swipe (touch) ---------- */
  // Arrows alone are not how people move through images on a phone, so the stage
  // also takes a horizontal drag. The axis is locked on the first few pixels of
  // movement: vertical drags stay page scroll and never steal a slide.
  var stage = mainImg && mainImg.parentNode;
  if (stage && media.length > 1 && 'ontouchstart' in window) {
    // pan-y lets the browser keep vertical scrolling while we own the x axis.
    stage.style.touchAction = 'pan-y';
    // The drag translates the image, so the frame has to clip. Every section
    // written so far sets overflow:hidden on the stage itself; this only covers
    // a future generated page that forgets to, where the image would otherwise
    // visibly bleed past the frame edge mid-drag. Conditional so a stage that
    // deliberately lets something hang outside keeps its own choice.
    if (getComputedStyle(stage).overflowX === 'visible') stage.style.overflow = 'hidden';

    var swipeX = 0, swipeY = 0, swipeAxis = null, swiping = false;
    var SWIPE_LOCK = 10;   // px of movement before we decide the axis
    var SWIPE_TRIGGER = 45; // px of horizontal travel that changes the slide

    function swipeReset() {
      swiping = false;
      swipeAxis = null;
      mainImg.style.transition = '';
      mainImg.style.transform = '';
    }

    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { swipeReset(); return; }
      swiping = true;
      swipeAxis = null;
      swipeX = e.touches[0].clientX;
      swipeY = e.touches[0].clientY;
      mainImg.style.transition = 'none';
    }, { passive: true });

    stage.addEventListener('touchmove', function (e) {
      if (!swiping || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - swipeX;
      var dy = e.touches[0].clientY - swipeY;
      if (!swipeAxis) {
        if (Math.abs(dx) < SWIPE_LOCK && Math.abs(dy) < SWIPE_LOCK) return;
        swipeAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (swipeAxis !== 'x') return;
      // Owning the gesture: stop the page scrolling under the drag.
      if (e.cancelable) e.preventDefault();
      // Damped follow so the image tracks the finger without sliding fully out.
      mainImg.style.transform = 'translateX(' + (dx * 0.35) + 'px)';
    }, { passive: false });

    stage.addEventListener('touchend', function (e) {
      if (!swiping) return;
      var dx = swipeAxis === 'x' && e.changedTouches.length
        ? e.changedTouches[0].clientX - swipeX
        : 0;
      swipeReset();
      if (Math.abs(dx) >= SWIPE_TRIGGER) setSlide(dx < 0 ? galIdx + 1 : galIdx - 1);
    }, { passive: true });

    stage.addEventListener('touchcancel', swipeReset, { passive: true });
  }

  /* ---------- sticky bar reveal ---------- */
  // The bar only earns its space once the real Add to Cart has scrolled away.
  var stickyBar = root.querySelector('[data-pdpf-sticky], .sticky-bar');
  var buyboxEl = root.querySelector('[data-pdpf-buybox], #akina-buybox');
  if (stickyBar && buyboxEl && 'IntersectionObserver' in window) {
    var stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // bottom <= 0 distinguishes "scrolled past it" from "not reached it yet".
        // Without that test the bar would also appear above the buy box, e.g. when
        // a ?variant= deep link lands the page mid-document.
        var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        stickyBar.classList.toggle('is-visible', scrolledPast);
      });
    }, { threshold: 0 });
    stickyObserver.observe(buyboxEl);
  } else if (stickyBar) {
    // No IntersectionObserver: fall back to always-on rather than never-on.
    stickyBar.classList.add('is-visible');
  }

  /* ---------- reveal a long collapsed list (reviews) ---------- */
  // Incremental: each press uncovers the next batch rather than the whole list, so
  // a reader who wants a few more is not dropped into forty cards at once. Hidden
  // cards are found by class on every press instead of being cached, so the batch
  // maths cannot drift from what is actually on screen.
  var revealBtn = root.querySelector('[data-pdpf-reveal]');
  if (revealBtn) {
    var revealTarget = document.getElementById(revealBtn.getAttribute('aria-controls'));
    if (revealTarget) {
      var BATCH = 10;
      var initiallyHidden = revealTarget.querySelectorAll('.is-hidden').length;

      var collapse = function () {
        var shown = revealTarget.querySelectorAll('.review-card');
        for (var i = shown.length - initiallyHidden; i < shown.length; i++) {
          shown[i].classList.add('is-hidden');
        }
        revealBtn.textContent = 'Show more reviews';
        revealBtn.setAttribute('aria-expanded', 'false');
        if (revealTarget.scrollIntoView) {
          revealTarget.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      };

      revealBtn.addEventListener('click', function () {
        if (revealBtn.dataset.exhausted === 'true') { collapse(); delete revealBtn.dataset.exhausted; return; }
        var hidden = revealTarget.querySelectorAll('.is-hidden');
        for (var i = 0; i < BATCH && i < hidden.length; i++) {
          hidden[i].classList.remove('is-hidden');
        }
        revealBtn.setAttribute('aria-expanded', 'true');
        if (!revealTarget.querySelectorAll('.is-hidden').length) {
          // Nothing left to add — offer the way back instead of a dead button.
          revealBtn.dataset.exhausted = 'true';
          revealBtn.textContent = 'Show fewer reviews';
        }
      });
    }
  }

  /* ---------- accordions ---------- */
  root.querySelectorAll('.acc .acc-q, [data-pdpf-accordion] > button').forEach(function (q) {
    q.addEventListener('click', function () {
      var open = q.parentNode.classList.toggle('open');
      q.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  /* ---------- add to cart ---------- */
  // Rendered inline rather than through window.alert: a modal dialog blocks the
  // page (and deadlocks headless verification), and "Cannot find variant" during
  // review usually just means the product is still a draft, not a wiring bug.
  function showError(btn, message) {
    var box = btn.parentNode.querySelector('[data-pdpf-error]');
    if (!box) {
      box = document.createElement('p');
      box.setAttribute('data-pdpf-error', '');
      box.setAttribute('role', 'alert');
      box.style.cssText = 'margin:8px 0 0;font-size:13px;color:#b3261e;';
      btn.parentNode.insertBefore(box, btn.nextSibling);
    }
    box.textContent = message || 'Sorry, that could not be added to your cart.';
  }

  function clearError(btn) {
    var box = btn.parentNode.querySelector('[data-pdpf-error]');
    if (box) box.remove();
  }

  atcEls.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var v = resolveVariant();
      if (!v || !v.available) return;

      clearError(btn);
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      // /cart/add.js only honours `sections` (the request for rendered section HTML
      // back in the same response) on a multipart body — a JSON body with the same
      // fields silently omits `sections` from the response entirely. product-form.js
      // sends FormData for exactly this reason; matched here rather than guessed.
      var fd = new FormData();
      fd.append('id', v.id);
      fd.append('quantity', qty);
      if (cartUI) {
        // One comma-joined field, not one field per section — /cart/add.js only
        // returns HTML for the section named in the LAST occurrence of a repeated
        // `sections` field, so appending each id separately silently drops all but
        // one. product-form.js gets this right by accident: FormData.append coerces
        // an array argument to a comma-joined string.
        fd.append('sections', cartUI.getSectionsToRender().map(function (s) { return s.id; }).join(','));
        fd.append('sections_url', window.location.pathname);
        cartUI.setActiveElement(btn);
      }

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) throw new Error(json.description || json.message || 'Add to cart failed');
            return json;
          });
        })
        .then(function (response) {
          btn.disabled = false;
          btn.textContent = original;
          if (cartUI) {
            // renderContents() only swaps section HTML — it never clears "is-empty"
            // from the cart element itself. product-form.js does that separately in
            // its own success handler; skip it and the drawer's open() reads a stale
            // is-empty on the very first add of a session, walks into the (now-gone)
            // empty-state markup instead of #CartDrawer, and throws on a null focus
            // target. Matched here for the same reason it exists there.
            if (cartUI.classList.contains('is-empty')) cartUI.classList.remove('is-empty');
            cartUI.renderContents(response);
          } else {
            window.location.href = cartUrl;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdpf', productVariantId: v.id, cartData: response });
          }
        })
        .catch(function (err) {
          console.error('[pdpf] add to cart failed', err);
          btn.disabled = false;
          btn.textContent = original;
          showError(btn, err.message);
        });
    });
  });

  /* ---------- init ---------- */
  // Slide 0 is the product's featured media. Only a deep link to a specific
  // variant should override it; a defaulted selection should not.
  var initIdx = arrivedWithVariant ? mediaIndexForVariant(resolveVariant()) : -1;
  setSlide(initIdx >= 0 ? initIdx : 0);
  render();
})();
